import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import CreateSidebar from './CreateSidebar';
import Canvas from './Canvas';
import EditSidebar from './EditSidebar';
import styles from './styles/CreateScenario.module.css';
import * as ScenarioAPI from '../../../services/ScenarioServices';
import TopBar from './TopBar';
import useIsMobile from '../../../utils/useIsMobile';
import { TutorialCanvasBridge } from '../../../tutorials';
import { useCanvas } from '../../../context/CanvasContext';
import { useEditorUI } from '../../../context/EditorUIContext';

/**
 * The editor's layout.
 *
 * It mounts no providers: Router.js already wraps this route in
 * ReactFlowProvider and CanvasProvider. It used to mount a second
 * CanvasProvider inside them, which meant two independent copies of the editor
 * state — the one this subtree read and the one everything else read.
 */
const CreateScenario = () => {
    const { scenarioId } = useParams();
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const isMobile = useIsMobile();

    const [editSidebarWidth, setEditSidebarWidth] = useState(20);
    const [renderEditSidebar, setRenderEditSidebar] = useState(true);

    const handleEditSidebarResize = (event) => {
        event.preventDefault();
        document.onmousemove = (moveEvent) => {
            const newWidth = ((window.innerWidth - moveEvent.clientX) / window.innerWidth) * 100;
            if (newWidth > 20 && newWidth < 40) setEditSidebarWidth(newWidth);
        };
        document.onmouseup = () => {
            document.onmousemove = null;
        };
    };

    useEffect(() => {
        if (isMobile) setRenderEditSidebar(false);
    }, [isMobile]);

    const location = useLocation();
    const navigate = useNavigate();
    const { loading: canvasLoading, getNode } = useCanvas();
    const { selectItem, setExpandedEditorNodeId } = useEditorUI();

    /**
     * Picks up where a preview left off. Previewing is a route change, so the
     * editor — selection and all — is rebuilt on the way back; the preview
     * hands back which situation was open, and whether in the expanded editor.
     * Read once at mount and dropped from history straight after, so a refresh
     * or a later back-navigation does not re-apply it.
     */
    const restoreRef = useRef(location.state?.restoreSelection ?? null);
    useEffect(() => {
        const restore = restoreRef.current;
        if (!restore || canvasLoading) return;
        restoreRef.current = null;
        navigate(location.pathname, { replace: true, state: null });

        if (!restore.nodeId || !getNode(restore.nodeId)) return;
        selectItem('situation', restore.nodeId);
        setRenderEditSidebar(true);
        if (restore.expanded) setExpandedEditorNodeId(restore.nodeId);
    }, [canvasLoading, getNode, selectItem, setExpandedEditorNodeId, navigate, location.pathname]);

    useEffect(() => {
        const verifyOwnership = async () => {
            try {
                await ScenarioAPI.verifyScenarioOwnership(scenarioId);
                setIsAuthorized(true);
            } catch (err) {
                console.error('Ownership verification failed:', err);
                setIsAuthorized(false);
                setError(err.response?.status === 404 ? 'Scenario not found.' : 'Access Denied');
            } finally {
                setLoading(false);
            }
        };

        verifyOwnership();
    }, [scenarioId]);

    if (loading) return <div>Loading...</div>;
    if (error) return <div>{error}</div>;
    if (!isAuthorized) return null;

    return (
        <>
            {/* Renders nothing; lends a running tutorial the editor's actions so
                a step can pre-fill what the user just dropped. */}
            <TutorialCanvasBridge />
            <TopBar
                renderEditSidebar={renderEditSidebar}
                setRenderEditSidebar={setRenderEditSidebar}
                isMobile={isMobile}
            />
            <div className={styles.container}>
                <CreateSidebar
                    isMobile={isMobile}
                    renderEditSidebar={renderEditSidebar}
                    setRenderEditSidebar={setRenderEditSidebar}
                />

                {(!isMobile || !renderEditSidebar) && (
                    <div
                        className={styles.canvas}
                        style={{
                            width: isMobile
                                ? '100%'
                                : `${100 - (renderEditSidebar ? editSidebarWidth : 0)}%`,
                        }}
                    >
                        <Canvas setRenderEditSidebar={setRenderEditSidebar} isMobile={isMobile} />
                    </div>
                )}

                {!isMobile && renderEditSidebar && (
                    <>
                        <div className={styles.resizer} onMouseDown={handleEditSidebarResize} />
                        <div className={styles.editSidebar} style={{ width: `${editSidebarWidth}%` }}>
                            <EditSidebar setRenderEditSidebar={setRenderEditSidebar} />
                        </div>
                    </>
                )}

                {isMobile && renderEditSidebar && (
                    <div className={styles.editSidebar} style={{ width: '100%' }}>
                        <EditSidebar setRenderEditSidebar={setRenderEditSidebar} />
                    </div>
                )}
            </div>
        </>
    );
};

export default CreateScenario;
